/**
 * Kick the /campaign-analytics request off BEFORE the route chunk has landed.
 *
 * WHY: /analytics/campaigns/:campaignId is 128.4 kB gz, 89% of which is `vendor-charts`
 * (recharts, ~114 kB gz). CampaignAnalyticsPage.jsx fires its fetch from a MOUNT EFFECT,
 * so the sequence was strictly serial:
 *
 *   route chunk + recharts download → parse → mount → effect → fetch → render
 *
 * Everything this request needs is already in the URL, so it can start at the same
 * moment the chunk request does. components/App.jsx calls `primeFromLocation()` from the
 * route's `lazy()` factory; the component then consumes the in-flight promise instead of
 * issuing its own. Same trick routes/popup.jsx uses for `chunks_ready`.
 *
 * ⚠️ NOTHING HEAVY MAY BE IMPORTED HERE. This module is statically imported by App.jsx,
 * i.e. it lives in the ENTRY chunk. It deliberately has zero imports — pulling anything
 * from components/Analytics/** would drag recharts eager and invert the whole point.
 *
 * ⚠️ The primed promise NEVER REJECTS. It is created inside a `lazy()` factory, where an
 * escaping rejection would fail the route chunk load, and it may end up never consumed
 * (the merchant navigates away), which would surface as an unhandled rejection. Failures
 * are resolved as `{ error }` and re-thrown by the consumer inside its own try/catch.
 */

/** key → Promise<{ response?: Response, error?: unknown }> */
const PRIMED = new Map();

const keyFor = (campaignId, timeframe, viewMode) =>
  `${campaignId}|${timeframe}|${viewMode}`;

/**
 * The ONE definition of this request. Both the primed path and the component's own
 * fetch go through here so the two can never drift — a drifting URL would silently
 * miss the cache key and quietly restore the serial behaviour.
 *
 * ⚠️ `cache: 'no-store'` and NO `_t=Date.now()` buster. The two used to be applied
 * together; the buster made every URL unique, which defeats even a back-navigation
 * bfcache hit AND would make the primed key unmatchable. `no-store` alone is the
 * freshness guarantee that was actually wanted.
 */
export function requestCampaignAnalytics(campaignId, timeframe, viewMode) {
  const params = new URLSearchParams({ campaignId, timeframe, viewMode });
  return fetch(`/campaign-analytics?${params}`, {
    method: "GET",
    credentials: "include",
    cache: "no-store",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
}

export function primeCampaignAnalytics(campaignId, timeframe, viewMode) {
  if (!campaignId) return;
  const key = keyFor(campaignId, timeframe, viewMode);
  if (PRIMED.has(key)) return;
  PRIMED.set(
    key,
    requestCampaignAnalytics(campaignId, timeframe, viewMode).then(
      (response) => ({ response }),
      (error) => ({ error })
    )
  );
}

/**
 * One-shot. The `Response` body can only be read once, so a primed entry is removed as
 * it is handed out — a second caller (StrictMode's double effect invoke, a timeframe
 * change) correctly falls through to a fresh request.
 */
export function consumeCampaignAnalytics(campaignId, timeframe, viewMode) {
  const key = keyFor(campaignId, timeframe, viewMode);
  const primed = PRIMED.get(key);
  if (primed) PRIMED.delete(key);
  return primed ?? null;
}

/**
 * Derive the campaign id and timeframe from the URL the browser is already on.
 *
 * ⚠️ The defaults MUST match CampaignAnalyticsPage's initial state exactly
 * (`timeframe = searchParams.get('timeframe') || '30d'`, `viewMode = 'auto'`), or the
 * component's key will not match the primed one and the prefetch is silently wasted —
 * a failure mode that looks identical to it working.
 */
export function primeFromLocation(pathname, search) {
  // Wrapped so this can never throw: it is called from a React.lazy() factory, where a
  // synchronous throw would fail the route chunk load outright. A warm-up that quietly
  // does nothing is the correct degradation — the page still fetches for itself.
  try {
    const match = /^\/analytics\/campaigns\/([^/?#]+)/.exec(pathname || "");
    if (!match) return;
    const campaignId = decodeURIComponent(match[1]);
    const timeframe = new URLSearchParams(search || "").get("timeframe") || "30d";
    primeCampaignAnalytics(campaignId, timeframe, "auto");
  } catch {
    /* warm-up only — never let this break the route */
  }
}
